import { useMsal } from "@azure/msal-react";
import {
    ChoiceGroup,
    DefaultButton,
    Dropdown,
    FontWeights, IDropdownOption,
    IconButton,
    IPanelProps,
    IRenderFunction,
    Label,
    Panel,
    PanelType,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
    Selection,
    SelectionMode,
    IObjectWithKey
} from "@fluentui/react";
import { RootState } from "app/store";
import SimpleCard, { ECardIcon } from "components/controls/SimpleCard";
import { Tier1To4, createBenefit, createDefaultCategoryFields, emitCustomEvent, fadeIn, panelButtonsStyles, sectionStackTokens } from "global/Application";
import { useApplication } from "global/Application/Context";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { IPlanBenefitRelation, addBenefitReference, addUpdateBenefit, importPlanBenefits } from "global/Reducer/dataSlice";
import { JsonTransformer } from "global/Transform/JsonTransformer";
import { updateArrayReplace } from "global/Utility/TSFunctions";
import {
    bcbs_config_category as IXrmTypes_bcbs_config_category,
    bcbs_config_supercategory as IXrmTypes_bcbs_config_supercategory,
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot
} from "model/CRM";
import { IPanelEvent, IPickItem, IValueChoiceGroupOption } from "model/Extension";
import {
    EBenefitStatus as FAM_EBenefitStatus,
    IBenefit as FAM_IBenefit,
    IBenefitRef as FAM_IBenefitRef,
    IEntityReferenceValue as FAM_IEntityReferenceValue,
    IOptionSetValue as FAM_IOptionSetValue,
    IPlan as FAM_IPlan
} from "model/FlexibleAttributeModel";
import { useEffect, useState, useMemo, useRef } from "react";
import { ConnectedProps, connect, useDispatch } from "react-redux";
import TierWizard from "views/TierWizard";
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import BenefitCard, { EBenefitCardFunction } from "./BenefitCard";
import SnapshotLookup from "./Controls/SnapshotLookup";
import CustomDetailList from "components/controls/CustomDetailList/CustomDetailList";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import { IExtendedColumn } from "model/IExtendedColumn";
import { DefaultGridConfig } from "components/controls/CustomDetailList/CustomDetailList";
import { ICustomDetailListConfiguration } from "components/controls/CustomDetailList/ICustomDetailListConfiguration";
import { stampAssociationTemplateProvenance } from "global/Application/dataActions";

// TODO move to helper functions
const mapName = (name?: string) => {
    switch (name) {
        case 'Pharmacy':
            return 'BCBSRX';

        default:
            return name ?? "";
    }
}

interface IConfirm {
    benefitNew: FAM_IBenefit | null;
}

interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IConfirm>) => void;

    // start condition
    defaultPayload?: IConfirm;

    currentPlan?: FAM_IPlan;
}

const enum EView {
    entry,
    selectBenefit,
    add,
    addProperties,
    copy,
    import,
    importProduct,
    importAssociationProduct,
};

/**
 * Returns true if a template benefit has already been added to the current configuration.
 * Matches by original template ID (bcbs_associationtemplatebenefitid) or by category + name.
 */
function isTemplateBenefitAlreadyAdded(
    templateBenefit: FAM_IBenefit,
    existingBenefits: FAM_IBenefit[]
): boolean {
    const originalId = (templateBenefit as any)._originalTemplateId as string | undefined;
    return existingBenefits.some(b => {
        const byId = !!originalId && b.bcbs_associationtemplatebenefitid === originalId;
        const categoryMatch = String(b.bcbs_config_categoryid ?? '') === String(templateBenefit.bcbs_config_categoryid ?? '');
        const nameMatch = b.name === templateBenefit.name;
        return byId || (categoryMatch && nameMatch);
    });
}



const BenefitWizard = (props: IProps) => {

    const dispatch = useDispatch();
    const { instance } = useMsal();
    const application = useApplication();

    const [loading, setLoading] = useState<Boolean>(false);

    // current view state/stages
    const [view, setView] = useState<EView | null>(EView.entry);

    // selected category

    const [optionEntry, setOptionEntry] = useState<IValueChoiceGroupOption<EView> | null>(null);

    const [newSuperCategory, setNewSuperCategory] = useState<IXrmTypes_bcbs_config_supercategory | null>(null);
    const [newCategory, setNewCategory] = useState<FAM_IBenefit | null>(null);
    const [newAvailableCategories, setNewAvailableCategories] = useState<IXrmTypes_bcbs_config_category[] | null>(null);

    const [importSnapshot, setImportSnapshot] = useState<IXrmTypes_bcbs_groupconfigurationsnapshot | null>();
    const [selectedBenefits, setSelectedBenefits] = useState<IPickItem<FAM_IBenefit>[] | null>(null);

    // Ref that always holds the latest benefits - used in canSelectItem to avoid stale closures
    const latestBenefitsRef = useRef<FAM_IBenefit[]>(props.state.data.json.benefits ?? []);
    useEffect(() => {
        latestBenefitsRef.current = props.state.data.json.benefits ?? [];
    });

    // Grid configuration for Association Template benefits
    const [benefitGridConfig, setBenefitGridConfig] = useState<ICustomDetailListConfiguration>(() => ({
        ...DefaultGridConfig(),
        pageSize: 10,
        page: 1
    }));

    // Force re-render when selection changes
    const [selectionVersion, setSelectionVersion] = useState(0);

    // Selection for the grid (Association Template)
    const benefitSelection = useMemo(() => {
        return new Selection({
            onSelectionChanged: () => {
                // Force re-render to update button state
                setSelectionVersion(v => v + 1);
            },
            canSelectItem: (item: IObjectWithKey) => {
                const gridItem = item as IGridItem<FAM_IBenefit>;
                const existingBenefits = latestBenefitsRef.current.filter((b: FAM_IBenefit) => !b.deactivated);
                return !isTemplateBenefitAlreadyAdded(gridItem.data!, existingBenefits);
            }
        });
    }, []);

    // Memoize grid items for Association Template benefits
    const benefitGridItems = useMemo(() => {
        if (view === EView.importAssociationProduct && selectedBenefits) {
            const existingBenefits = props.state.data.json.benefits.filter((b: FAM_IBenefit) => !b.deactivated);

            const isAdded = (benefit: FAM_IBenefit) => isTemplateBenefitAlreadyAdded(benefit, existingBenefits);

            return [...selectedBenefits]
                .sort((a, b) => {
                    const aAdded = isAdded(a.item) ? 0 : 1;
                    const bAdded = isAdded(b.item) ? 0 : 1;
                    return aAdded - bAdded;
                })
                .map((b, index) => ({
                    key: b.item.id,
                    name: b.item.name || '',
                    data: b.item,
                    _index: index
                } as IGridItem<FAM_IBenefit>));
        }
        return [];
    }, [selectedBenefits, view, props.state.data.json.benefits]);

    // Sync Selection with grid items
    useEffect(() => {
        if (view === EView.importAssociationProduct && benefitGridItems.length > 0) {
            // Set items in Selection object - required for selection to work with pagination
            benefitSelection.setItems(benefitGridItems, true);
        }
    }, [benefitGridItems, view, benefitSelection]);

    // Paged slice of grid items - DetailsList renders this while Selection tracks the full list
    const benefitGridItemsPaged = useMemo(() => {
        const pageSize = benefitGridConfig.pageSize ?? 10;
        const page = benefitGridConfig.page ?? 1;
        const startIndex = (page - 1) * pageSize;
        return benefitGridItems.slice(startIndex, startIndex + pageSize);
    }, [benefitGridItems, benefitGridConfig.pageSize, benefitGridConfig.page]);

    // Track if any benefits are selected (for footer button state)
    const hasSelectedBenefits = useMemo(() => {
        if (view === EView.importAssociationProduct) {
            // For grid view, check Selection
            return benefitSelection.getSelectedCount() > 0;
        } else {
            // For card view, check selectedBenefits state
            return selectedBenefits && selectedBenefits.some(q => q.selected);
        }
    }, [selectedBenefits, selectionVersion, view, benefitSelection]);

    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    useEffect(() => {
        // any props that we need to build state on component mount?
        // reset on start
        setNewCategory(props.defaultPayload?.benefitNew ?? null);
        setNewSuperCategory(null);
        setView(EView.entry);

    }, [props.isOpen]);


    useEffect(() => {

        if (view === EView.copy)
        // reload collection if not populated already?
        {
            (async () => {
                try {
                    setLoading(true);

                    const transformer = JsonTransformer.getTransformerForCopyInternal(
                        props.state.data.config,
                        dataSource,
                        props.state.data.snapshot,
                        { applyToPlans: true, applyToBenefits: true }
                    );

                    const jClone = structuredClone(props.state.data.json)
                    const sClone = structuredClone(props.state.data.snapshot)

                    await transformer.transformAsync(jClone, sClone);

                    const data = jClone;

                    const benefits: IPickItem<FAM_IBenefit>[] = [];

                    // get the actual benefit objects already linked to the current plan
                    // (the copy transformer replaces all IDs, so we match using the same
                    // logic as BenefitComparisonPanel: bcbs_config_categoryid + name)
                    const currentPlanBenefits = props.state.data.json.benefits.filter(b =>
                        props.currentPlan?.benefitRefs.some(r => r.id === b.id)
                    );

                    data.benefits.forEach(eachBenefit => {

                        // transformer above is already taking care of this
                        // lets skip deactivated, they shouldn't be able to pick
                        if (eachBenefit.deactivated)
                            return;

                        // skip benefits already linked to the current plan
                        // consistent with BenefitComparisonPanel isAdded logic
                        const alreadySelected = currentPlanBenefits.some(b => {
                            const categoryMatch = String(b.bcbs_config_categoryid ?? '') === String(eachBenefit.bcbs_config_categoryid ?? '');
                            const nameMatch = b.name === eachBenefit.name;
                            return categoryMatch && nameMatch;
                        });

                        if (alreadySelected)
                            return;

                        benefits.push({
                            item: eachBenefit,
                            selected: false
                        });
                    });

                    setSelectedBenefits(benefits);
                } catch (error) {

                    // lets create a new one is payload is not providing one
                    emitCustomEvent({
                        eventType: 'globalException',
                        details: error
                    });

                } finally {
                    setLoading(false);
                }

            })();
        }
        else if (importSnapshot) {
            (async () => {

                setLoading(true);

                try {
                    const result = await dataSource.downloadGroupConfigurationSnapshot(importSnapshot);

                    if (!result)
                        throw new Error('Snapshot not found!');

                    // Stamp original IDs before ReplaceIdsTransformer replaces them with new UUIDs,
                    // so isAdded can match against bcbs_associationtemplatebenefitid after transformation.
                    if (view === EView.importAssociationProduct) {
                        result.json.benefits.forEach(b => {
                            if (b.id) (b as any)._originalTemplateId = b.id;
                        });
                    }

                    const transformer = JsonTransformer.getTransformerForCopy(
                        props.state.data.config,
                        dataSource,
                        props.state.data.snapshot,
                        { applyToPlans: true, applyToBenefits: true }
                    );

                    await transformer.transformAsync(result.json, importSnapshot);

                    const data = result.json;

                    const benefits: IPickItem<FAM_IBenefit>[] = [];

                    // get the actual benefit objects already linked to the current plan
                    // consistent with BenefitComparisonPanel isAdded logic: bcbs_config_categoryid + name
                    const currentPlanBenefits = props.state.data.json.benefits.filter(b =>
                        props.currentPlan?.benefitRefs.some(r => r.id === b.id)
                    );

                    data.benefits.forEach(eachBenefit => {

                        // transformer above is already taking care of this
                        // lets skip deactivated, they shouldn't be able to pick
                        if (eachBenefit.deactivated)
                            return;

                        // For Association Product: show ALL benefits in the grid (check icon marks already-added ones)
                        // For other import flows: skip benefits already linked to the current plan
                        if (view !== EView.importAssociationProduct) {
                            const alreadySelected = currentPlanBenefits.some(b => {
                                const categoryMatch = String(b.bcbs_config_categoryid ?? '') === String(eachBenefit.bcbs_config_categoryid ?? '');
                                const nameMatch = b.name === eachBenefit.name;
                                return categoryMatch && nameMatch;
                            });

                            if (alreadySelected)
                                return;
                        }

                        benefits.push({
                            item: eachBenefit,
                            selected: false
                        });
                    });

                    setSelectedBenefits(benefits);

                } catch (error) {

                    emitCustomEvent({
                        eventType: 'globalException',
                        details: error
                    });

                } finally {
                    setLoading(false);
                }
            })();
        }

    }, [importSnapshot, view]);

    const optionsEntry: IValueChoiceGroupOption<EView>[] = [
        {
            key: 'add',
            text: 'Add - New',
            data: EView.add,
            disabled: !!props.currentPlan
        },
        {
            key: 'pick',
            text: 'Pick - From Current Configuration',
            data: EView.selectBenefit,
            disabled: !props.currentPlan || !application.ChangeTypePermissions.plan.link_benefit
        },
        {
            key: 'copy',
            text: 'Copy/Clone - From Current Configuration',
            data: EView.copy
        },
        {
            key: 'import',
            text: 'Copy/Clone - From External Configuration/BlueRelationship',
            data: EView.import,
            disabled: false
        },
        {
            key: 'importProduct',
            text: 'Select As Product',
            data: EView.importProduct,
            disabled: false
        },
        {
            key: 'importAssociationProduct',
            text: 'Select As Association Product',
            data: EView.importAssociationProduct,
            disabled: false
        },
    ];

    function onRenderEntry(): JSX.Element {
        return <div>
            <ChoiceGroup
                options={optionsEntry}
                label="Choose Source"
                required={true}
                selectedKey={optionEntry?.key}
                onChange={(ev, option?: IValueChoiceGroupOption<EView>) => {
                    setOptionEntry(option ?? null);
                }}
            />
        </div >;
    }

    function onRenderEntryFooter(): JSX.Element | null {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={optionEntry === null}
                onClick={() => {
                    setView(optionEntry?.data ?? null);
                    setSelectedBenefits(null);
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => { props.onCancel?.(); }}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    function onRenderSelectBenefit(): JSX.Element {

        const currentPlanBenefits = props.state?.data.json?.benefits.filter(q => props.currentPlan?.benefitRefs.some(w => w.id === q.id)) ?? null;

        const currentPlanCategories = props.state?.data.config?.categories?.filter(q => currentPlanBenefits.some(w => w?.bcbs_config_categoryid === q.bcbs_config_categoryid)) ?? null;

        if (!selectedBenefits) {
            const benefits = props.state.data.json.benefits.map(t => {

                const ret: IPickItem<FAM_IBenefit> = {
                    item: t,
                    selected: false
                }

                return ret;
            })

            setSelectedBenefits(benefits);

            return (
                <div>
                    Not Available
                </div>
            );
        }

        return (
            <div className={fadeIn}>
                <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                    Choose benefit
                </Text>
                <Stack
                    horizontal
                    horizontalAlign="start"
                    tokens={sectionStackTokens}
                    wrap
                    verticalFill
                >
                    {selectedBenefits.map(eachBenefit => {

                        if (eachBenefit.item.deactivated)
                            return <></>

                        return (
                            <BenefitCard
                                state={props.state}
                                benefit={eachBenefit.item}
                                benefitCollection={selectedBenefits}
                                currentPlan={props.currentPlan ?? null}
                                currentPlanBenefits={currentPlanBenefits}
                                currentPlanCategories={currentPlanCategories}
                                mode={EBenefitCardFunction.addToPlan}
                                icon={ECardIcon.link}
                                onClick={() => {
                                    // benefit was already picked? remove it from the list
                                    const newState = updateArrayReplace(
                                        selectedBenefits,
                                        eachBenefit,
                                        {
                                            ...eachBenefit,
                                            selected: !eachBenefit.selected
                                        });

                                    setSelectedBenefits(newState);
                                }}
                            />
                        );
                    })}
                </Stack>
                <Separator />
            </div>
        );
    }

    function onRenderSelectBenefitFooter(): JSX.Element | null {
        return <div className={fadeIn}>
            <PrimaryButton text="Continue"
                styles={panelButtonsStyles}
                disabled={!selectedBenefits || selectedBenefits?.length === 0}
                onClick={() => {
                    // any benefit picked?
                    if (selectedBenefits && selectedBenefits.length > 0 && props.currentPlan) {
                        const plan = props.currentPlan;

                        const t = selectedBenefits
                            .filter(q => q.selected)
                            .map(eB => {
                                const benefitRef: FAM_IBenefitRef = {
                                    id: eB.item.id,
                                    name: eB.item.name
                                };

                                return { plan: plan, benefitRef: benefitRef }
                            });

                        dispatch(addBenefitReference(t));
                        setSelectedBenefits([]);
                        props.onDismiss?.();
                    }
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    function onRenderAdd(): JSX.Element {
        if (newSuperCategory && (newAvailableCategories?.length ?? 0) > 1)
            return onRenderAddCategory();

        return onRenderAddSuperCategory()

    }

    const setSuperCategoryAndCategory = (
        selectedSuperCategory: IXrmTypes_bcbs_config_supercategory | null,
        selectedCategory: IXrmTypes_bcbs_config_category | null
    ) => {

        if (!selectedSuperCategory) {
            setNewSuperCategory(null);
            setNewAvailableCategories(null);
            setNewCategory(null);
            return;
        }

        const bcbs_config_categories_by_supercategoryid = props.state.data.config.cache.map_bcbs_config_categories_by_bcbs_config_supercategoryid[selectedSuperCategory.bcbs_config_supercategoryid!]

        const availableCategories = bcbs_config_categories_by_supercategoryid != null
            ? Object.entries(bcbs_config_categories_by_supercategoryid).map(([key, category]) => category)
            : [];

        let _selectedCategory: IXrmTypes_bcbs_config_category | null = null;

        if (selectedCategory)
            _selectedCategory = selectedCategory;
        else if (availableCategories.length === 1)
            _selectedCategory = availableCategories[0];

        // only one category available, auto select
        if (_selectedCategory != null) {

            const clone: FAM_IBenefit = createBenefit(props.state.data.config,
                {
                    category: _selectedCategory,
                    // if not setup as dual funded, then current plan/benefit needs to default to global version of funding type
                    fundingType: props.state.data.json.fundingType!,
                    grandFathered: _selectedCategory.bcbs_isgrandfatherenabled ? props.state.data.json.grandFathered! : null,
                }
            );

            setNewSuperCategory(selectedSuperCategory);
            setNewAvailableCategories(availableCategories);
            setNewCategory(clone);

        } else {
            setNewSuperCategory(selectedSuperCategory);
            setNewAvailableCategories(availableCategories);
            setNewCategory(null);
        }
    }

    function onRenderAddSuperCategory(): JSX.Element {

        return (
            <div className={fadeIn}>
                <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                    Choose benefit
                </Text>

                <Stack
                    horizontal
                    horizontalAlign="start"
                    tokens={sectionStackTokens}
                    wrap
                    verticalFill
                >
                    {props.state.data.config.bcbs_config_supercategories
                        // .filter(category => category._bcbs_config_segmentid_value === '69d33403-a832-ec11-b6e5-0022480ab88f')
                        //TODO: filtering to benefit segment
                        .map(superCategory => {

                            const sameCategory = false //currentPlanCategories.some(q => q.bcbs_config_categoryid === category.bcbs_config_categoryid)

                            const selected = newSuperCategory
                                ? superCategory.bcbs_config_supercategoryid === newSuperCategory.bcbs_config_supercategoryid
                                : false;

                            return (
                                <SimpleCard
                                    key={superCategory.bcbs_config_supercategoryid}
                                    description={superCategory.bcbs_name ?? ""}
                                    selected={selected}
                                    disabled={sameCategory}
                                    icons={ECardIcon.add}
                                    onClick={sameCategory
                                        ? undefined
                                        : () => {
                                            // no cateogry selected or different category selected
                                            if (!newSuperCategory || newSuperCategory.bcbs_config_supercategoryid !== superCategory.bcbs_config_supercategoryid)
                                                setSuperCategoryAndCategory(superCategory, null);
                                            else
                                                setSuperCategoryAndCategory(null, null);

                                        }}
                                />
                            );
                        })}

                </Stack>
            </div>
        );
    }

    function onRenderAddCategory(): JSX.Element {

        if (!newSuperCategory)
            return <div>Category not available.</div>

        const superCatCategories = newSuperCategory?.bcbs_config_supercategoryid ?
            props.state.data.config.cache.map_bcbs_config_categories_by_bcbs_config_supercategoryid[newSuperCategory.bcbs_config_supercategoryid]
            : null;

        const availableCategories = superCatCategories != null
            ? Object.entries(superCatCategories).map(([key, category]) => category)
            : [];

        const currentPlanBenefits = props.state?.data.json?.benefits.filter(q => props.currentPlan?.benefitRefs.some(w => w.id === q.id)) ?? null;
        const currentPlanCategories = props.state?.data.config?.categories?.filter(q => currentPlanBenefits.some(w => w?.bcbs_config_categoryid === q.bcbs_config_categoryid)) ?? null;



        return (
            <div className={fadeIn}>
                <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                    Choose benefit
                </Text>

                <SimpleCard
                    key={newSuperCategory.bcbs_config_supercategoryid}
                    description={newSuperCategory.bcbs_name ?? ""}
                    selected={true}
                    icons={ECardIcon.add}
                    onClick={() => {
                        setSuperCategoryAndCategory(null, null);
                    }}
                />

                <Separator />
                <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                    Choose Vendor
                </Text>

                <Stack
                    horizontal
                    horizontalAlign="start"
                    tokens={sectionStackTokens}
                    wrap
                    verticalFill
                >
                    {availableCategories.map(category => {

                        return (
                            <SimpleCard
                                key={category.bcbs_config_categoryid}
                                description={mapName(category.bcbs_name)}
                                selected={category.bcbs_config_categoryid === newCategory?.bcbs_config_categoryid}
                                icons={ECardIcon.add}
                                onClick={() => {
                                    if (newCategory === null || category.bcbs_config_categoryid !== newCategory?.bcbs_config_categoryid) {
                                        setSuperCategoryAndCategory(newSuperCategory, category);
                                    }
                                    else {
                                        setNewCategory(null);
                                    }
                                }}
                            />
                        );
                    })}
                </Stack>
            </div>
        );

    }

    function onRenderAddFooter(): JSX.Element | null {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={optionEntry === null || newCategory === null}
                onClick={() => { setView(EView.addProperties); }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    function onRenderAddProperties(): JSX.Element {

        if (!newCategory)
            return <div>Benefit not available.</div>

        const isTiered = newCategory.bcbs_istierenabled ?? false
        const isGrandFathered = newCategory.bcbs_isgrandfatherenabled ?? false;
        const isFundingTypeEnabled = newCategory.bcbs_isfundingtypeenabled ?? false;
        const isDualFunded = props.state.data.json.dualFunded?.value ?? false;

        return (
            <div className={fadeIn}>
                <Label>Benefit Properties</Label>
                <TextField
                    label="Benefit Name"
                    value={newCategory?.name ?? ""}
                    disabled={newCategory === null}
                    onChange={(event, value) => {
                        const t = { ...newCategory!, name: value ?? '' };
                        setNewCategory(t);
                    }}
                />

                <Separator />
                <TierWizard
                    disabled={!isTiered}
                    variant={'new'}
                    benefit={newCategory}
                    defaultValue={{
                        tiers: newCategory.tiers,
                        tierSet: props.state.data.config.tiersets.find(t => t.bcbs_config_tiersetid === newCategory.tierSetRef?.id)!
                    }}
                    onChange={(value) => {
                        if (value.tiers && value.tierSet) {
                            const t: FAM_IBenefit = {
                                ...newCategory!,
                                tiers: value.tiers ?? [],
                                tierSetRef: {
                                    id: value.tierSet.bcbs_config_tiersetid!,
                                    name: value.tierSet.bcbs_name!
                                }
                            };
                            setNewCategory(t);
                        }
                    }}
                />
                {!isTiered &&
                    <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Tier support has not been enabled for this category.</Text>
                }

                <Separator />
                <Dropdown
                    label={"Funding Type"}
                    options={props.state.data.config.cache.options_bcbs_fundingtypes ?? []}
                    disabled={!isDualFunded || !isFundingTypeEnabled}
                    selectedKey={newCategory?.fundingType?.value}
                    onChange={(event, option?: IDropdownOption<FAM_IOptionSetValue>) => {
                        const t: FAM_IBenefit = { ...newCategory, fundingType: option?.data ?? null }
                        setNewCategory(t);
                    }}
                />
                {!isFundingTypeEnabled &&
                    <div>
                        <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Funding Type support has not been enabled for this category.</Text>
                    </div>
                }
                {!isDualFunded &&
                    <div>
                        <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Group configuration has not been setup for dual funding.</Text>
                    </div>
                }
                <Separator />
                <Dropdown
                    label={"Grandfathered"}
                    options={props.state.data.config.cache.options_bcbs_grandfatheredcode ?? []}
                    disabled={!isGrandFathered}
                    selectedKey={newCategory?.grandFathered?.value}
                    onChange={(_, option?: IDropdownOption<FAM_IOptionSetValue>) => {
                        const t: FAM_IBenefit = { ...newCategory, grandFathered: option?.data ?? null }
                        setNewCategory(t);
                    }}
                />
                {!isGrandFathered &&
                    <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Grandfathered support has not been enabled for this category.</Text>
                }
            </div>
        );
    }

    function onRenderAddPropertiesFooter(): JSX.Element | null {
        if (!newCategory)
            return <div>Benefit not available.</div>

        const disable = optionEntry === null ||
            (newCategory.bcbs_istierenabled && newCategory.tierSetRef === null) ||
            (newCategory.bcbs_istierenabled && newCategory.tiers.length > 0 && newCategory.tiers.some(q => Tier1To4.includes(q.name) || q.name === '' || q.name === null));

        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={disable}
                onClick={() => {
                    let preventDefault = false;

                    const confirm: IPanelEvent<IConfirm> = {
                        preventDefault: ({ validationMessage }) => {
                            preventDefault = true;
                        },
                        value: {
                            benefitNew: newCategory,
                        }
                    };

                    props.onConfirm?.(confirm);


                    if (preventDefault)
                        return;

                    const fields = createDefaultCategoryFields(props.state.data.config, newCategory, newCategory.name, newCategory.tiers);

                    const benefit: FAM_IBenefit = {
                        ...newCategory!,
                        fields: fields,
                        attributeSets: [],
                        nonStandards: [],
                        status: props.state.data.config.cache.options_benefitStatusReasons.find(q => q.data?.value === FAM_EBenefitStatus["In-Progress"])?.data!,
                    }


                    dispatch(addUpdateBenefit([benefit]));
                    setNewCategory(null);

                    if (props.currentPlan) {
                        const benefitRef: FAM_IBenefitRef = {
                            id: benefit.id,
                            name: benefit.name,
                        };
                        dispatch(addBenefitReference([{ plan: props.currentPlan, benefitRef: benefitRef }]));
                        setSelectedBenefits([]);
                    }

                    setView(EView.entry);

                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.add)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    function onRenderCopy(): JSX.Element {

        const currentPlanBenefits = props.state?.data.json?.benefits.filter(q => props.currentPlan?.benefitRefs.some(w => w.id === q.id)) ?? null;

        const currentPlanCategories = props.state?.data.config?.categories?.filter(q => currentPlanBenefits.some(w => w?.bcbs_config_categoryid === q.bcbs_config_categoryid)) ?? null;

        // are we just displaying or limited to
        const mode: EBenefitCardFunction = props.currentPlan
            ? EBenefitCardFunction.addToPlan
            : EBenefitCardFunction.addToGroupConfiguration;

        return (
            <div className={fadeIn}>
                <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                    Choose benefit
                </Text>

                <Stack
                    horizontal
                    horizontalAlign="start"
                    tokens={sectionStackTokens}
                    wrap
                    verticalFill
                >
                    {
                        loading &&
                        <Spinner label="Please wait..." ariaLive="assertive" labelPosition="right" size={SpinnerSize.large} styles={{ root: { padding: '25px' } }} />
                    }

                    {selectedBenefits?.map(eachBenefit => {
                        return (
                            <BenefitCard
                                key={eachBenefit.item.id}
                                state={props.state}
                                benefit={eachBenefit.item}
                                benefitCollection={selectedBenefits}
                                currentPlan={props.currentPlan ?? null}
                                currentPlanBenefits={currentPlanBenefits}
                                currentPlanCategories={currentPlanCategories}
                                mode={mode}
                                icon={ECardIcon.copy}
                                onClick={() => {
                                    // benefit was already picked? remove it from the list
                                    const newState = updateArrayReplace(selectedBenefits, eachBenefit, { ...eachBenefit, selected: !eachBenefit.selected });
                                    setSelectedBenefits(newState);
                                }}
                            />
                        );
                    })}
                </Stack>
                <Separator />
            </div>
        );
    }

    function onRenderCopyFooter(): JSX.Element | null {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={!selectedBenefits?.find(t => t.selected)}
                onClick={() => {
                    const bSelects = selectedBenefits?.filter(q => q.selected).map(q => q.item) ?? [];
                    const bClones = bSelects

                    bClones.forEach(benefit => {
                        benefit.name = benefit.name + ' - Copied';
                        benefit.status = {
                            "type": "optionset",
                            "name": FAM_EBenefitStatus[FAM_EBenefitStatus["In-Progress"]],
                            "value": FAM_EBenefitStatus["In-Progress"]
                        }
                    });

                    dispatch(addUpdateBenefit(bClones));

                    // any benefit picked?
                    if (props.currentPlan) {
                        const plan = props.currentPlan;

                        const benefitRefs: IPlanBenefitRelation[] = bClones.map(benefit => {
                            const benefitRef: FAM_IBenefitRef = {
                                id: benefit.id,
                                name: benefit.name
                            };

                            return { plan: plan, benefitRef: benefitRef }
                        });

                        dispatch(addBenefitReference(benefitRefs));
                    }

                    setSelectedBenefits(null);
                    setImportSnapshot(null);

                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    function onRenderImport(): JSX.Element {

        // are we just displaying or limited to
        const mode: EBenefitCardFunction = props.currentPlan
            ? EBenefitCardFunction.addToPlan
            : EBenefitCardFunction.addToGroupConfiguration;

        let useAccount: FAM_IEntityReferenceValue | undefined = undefined;

        // Template Only account for "Select As Product"
        if (view === EView.importProduct) {
            useAccount = {
                type: 'entity',
                entityType: 'account',
                id: 'cb78766c-4ed5-ec11-a7b5-000d3a3ab609',
                name: 'Template Only',
            };
        }

        const currentPlanBenefits = props.state?.data.json?.benefits.filter(q => props.currentPlan?.benefitRefs.some(w => w.id === q.id)) ?? null;
        const currentPlanCategories = props.state?.data.config?.categories?.filter(q => currentPlanBenefits.some(w => w?.bcbs_config_categoryid === q.bcbs_config_categoryid)) ?? null;

        return <Stack tokens={{ childrenGap: '15' }}>

            <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                Copy benefits
            </Text>

            <SnapshotLookup
                onPick={async (value) => {
                    const snapshot = await dataSource.getGroupConfigurationSnapshot(value?.id!);
                    setImportSnapshot(snapshot);
                }}
                defaultValueAccount={useAccount}
                defaultValueViewKey={view === EView.importProduct ? "inProgressAndGoldStandardView" : undefined}
                valueAccount={useAccount}
                searchOnMount={true}
            />

            {
                loading &&
                <Spinner label="Please wait..." ariaLive="assertive" labelPosition="right" size={SpinnerSize.large} styles={{ root: { padding: '25px' } }} />
            }

            <Stack
                horizontal
                horizontalAlign="start"
                tokens={sectionStackTokens}
                wrap
                verticalFill
            >
                {selectedBenefits?.map(eachBenefit => {
                    return (
                        <BenefitCard
                            key={eachBenefit.item.id}
                            state={props.state}
                            benefit={eachBenefit.item}
                            benefitCollection={selectedBenefits}
                            currentPlan={props.currentPlan ?? null}
                            currentPlanBenefits={currentPlanBenefits}
                            currentPlanCategories={currentPlanCategories}
                            deactivated={eachBenefit.item.deactivated}
                            mode={mode}
                            icon={ECardIcon.copy}
                            onClick={() => {
                                // benefit was already picked? remove it from the list
                                const newState = updateArrayReplace(selectedBenefits, eachBenefit, { ...eachBenefit, selected: !eachBenefit.selected });
                                setSelectedBenefits(newState);
                            }}
                        />
                    );
                })}
            </Stack>
        </Stack>;
    }

    function onRenderImportAssociationProduct(): JSX.Element {

        const useAccount: FAM_IEntityReferenceValue = {
            type: 'entity',
            entityType: 'account',
            id: '09fb458d-49d8-eb11-bacb-000d3a533755',
            name: 'Association Template',
        };

        const benefitsForIsAdded = (props.state?.data.json?.benefits ?? []).filter((b: FAM_IBenefit) => !b.deactivated);

        return <Stack tokens={{ childrenGap: '15' }}>

            <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                Copy benefits
            </Text>

            <SnapshotLookup
                onPick={async (value) => {
                    const snapshot = await dataSource.getGroupConfigurationSnapshot(value?.id!);
                    setImportSnapshot(snapshot);
                }}
                defaultValueAccount={useAccount}
                defaultValueViewKey="associationTemplateView"
                valueAccount={useAccount}
                searchOnMount={true}
            />

            {
                loading &&
                <Spinner label="Please wait..." ariaLive="assertive" labelPosition="right" size={SpinnerSize.large} styles={{ root: { padding: '25px' } }} />
            }

            {benefitGridItems && benefitGridItems.length > 0 && (
                <CustomDetailList
                    gridConfig={[benefitGridConfig, setBenefitGridConfig]}
                    items={benefitGridItems}
                    itemsPaged={benefitGridItemsPaged}
                    columns={[
                        {
                            key: 'status',
                            name: 'Source',
                            fieldName: 'status',
                            minWidth: 48,
                            maxWidth: 48,
                            isResizable: false,
                            onRender: (item: IGridItem<FAM_IBenefit>) => {
                                const benefit = item.data!;
                                const isAdded = isTemplateBenefitAlreadyAdded(benefit, benefitsForIsAdded);
                                return (
                                    <IconButton
                                        iconProps={{ iconName: isAdded ? 'CheckMark' : 'Add' }}
                                        title={isAdded ? 'Already added' : 'Not yet added'}
                                        disabled={true}
                                        styles={{
                                            root: { width: '28px', height: '28px' },
                                            icon: { fontSize: '11px', color: isAdded ? '#0078d4' : '#a19f9d' }
                                        }}
                                    />
                                );
                            }
                        } as IExtendedColumn,
                        {
                            key: 'name',
                            name: 'Benefit Name',
                            fieldName: 'name',
                            minWidth: 200,
                            maxWidth: 1000,
                            isResizable: true,
                            onRender: (item: IGridItem<FAM_IBenefit>) => {
                                return item.data?.name;
                            }
                        } as IExtendedColumn
                    ]}
                    selectionMode={SelectionMode.multiple}
                    selection={benefitSelection}
                    selectionPreservedOnEmptyClick={true}
                    compact={true}
                />
            )}
        </Stack>;
    }

    function onRenderImportFooter(): JSX.Element | null {
        const handleContinueClick = () => {
            const benefits = selectedBenefits?.filter(q => q.selected).map(q => q.item)!;

            dispatch(importPlanBenefits({
                plan: props.currentPlan,
                benefits: benefits,
                config: props.state.data.config
            }));

            props.onDismiss?.();
        };

        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={!hasSelectedBenefits}
                onClick={handleContinueClick}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    function onRenderImportAssociationProductFooter(): JSX.Element | null {
        const handleContinueClick = () => {
            const selectedItems = benefitSelection.getSelection() as IGridItem<FAM_IBenefit>[];
            const benefits = selectedItems.map(item => {
                const benefit = JSON.parse(JSON.stringify(item.data)) as FAM_IBenefit;
                const originalId = (item.data as any)?._originalTemplateId as string | undefined;
                stampAssociationTemplateProvenance(benefit, originalId, importSnapshot?.bcbs_groupconfigurationsnapshotid);
                return benefit;
            });

            // dispatch directly so the reducer's "- Imported" suffix is not applied
            dispatch(addUpdateBenefit(benefits));

            if (props.currentPlan) {
                const benefitRefs: IPlanBenefitRelation[] = benefits.map(benefit => ({
                    plan: props.currentPlan!,
                    benefitRef: { id: benefit.id, name: benefit.name }
                }));
                dispatch(addBenefitReference(benefitRefs));
            }

            props.onDismiss?.();
        };

        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={!hasSelectedBenefits}
                onClick={handleContinueClick}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    // const onRenderHeader: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
    //     return (
    //         <div className={contextProps?.headerClassName} style={{ width: '100%', paddingLeft: '24px', fontSize: '24px', fontWeight: 'bold' }}>
    //             {view === EView.addProperties &&
    //                 <IconButton iconProps={{ iconName: 'NavigateBack' }} onClick={() => { setView(EView.add) }} />
    //             }
    //             {contextProps?.headerText}
    //         </div>
    //     );
    // }

    const onRenderBody: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;

        switch (view) {
            case EView.entry:
                onRender = onRenderEntry;
                break;

            case EView.selectBenefit:
                onRender = onRenderSelectBenefit;
                break;

            case EView.add:
                onRender = onRenderAdd;
                break;

            case EView.addProperties:
                onRender = onRenderAddProperties;
                break;

            case EView.copy:
                onRender = onRenderCopy;
                break;

            case EView.import:
                onRender = onRenderImport;
                break;

            case EView.importProduct:
                onRender = onRenderImport;
                break;

            case EView.importAssociationProduct:
                onRender = onRenderImportAssociationProduct;
                break;
        }

        return (
            <div >
                <Separator />
                {onRender()}
            </div>
        );

    }

    const onRenderUnknown: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        return (<div>Unknown</div>)
    }

    const onRenderFooterContent: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;

        switch (view) {
            case EView.entry:
                onRender = onRenderEntryFooter;
                break;

            case EView.add:
                onRender = onRenderAddFooter;
                break;

            case EView.addProperties:
                onRender = onRenderAddPropertiesFooter;
                break;

            case EView.selectBenefit:
                onRender = onRenderSelectBenefitFooter;
                break;

            case EView.copy:
                onRender = onRenderCopyFooter;
                break;

            case EView.import:
                onRender = onRenderImportFooter;
                break;

            case EView.importProduct:
                onRender = onRenderImportFooter;
                break;

            case EView.importAssociationProduct:
                onRender = onRenderImportAssociationProductFooter;
                break;
        }

        return onRender();

    }

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onCancel}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            headerText="Add | New Benefit"
            // onRenderHeader={onRenderHeader}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
        </Panel>
    )
}


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(BenefitWizard);
